import { useQuery } from '@tanstack/react-query';
import { TimesheetDto } from 'src/api/FiretrolApiClient';
import { timesheetApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

export interface TimesheetDetailParams {
  jobId: number;
  userId: number;
  weekId: number;
  year: number;
}

/**
 * @registry One user's timesheet for a job/week. The server returns an unsaved
 * draft, with the effective workflow resolved, when no row exists yet.
 */
export function useTimesheet(
  params: TimesheetDetailParams,
  options?: { enabled?: boolean },
) {
  const { jobId, userId, weekId, year } = params;
  const hasParams =
    jobId > 0 &&
    Number.isFinite(userId) &&
    Number.isFinite(weekId) &&
    Number.isFinite(year);

  return useQuery<TimesheetDto>({
    queryKey: queryKeys.timesheets.detail(jobId, userId, year, weekId),
    queryFn: ({ signal }) =>
      timesheetApiClient.getTimesheet(
        { jobId, userId, weekId, year },
        toCancelToken(signal),
      ),
    enabled: hasParams && options?.enabled !== false,
    staleTime: 0,
    meta: { errorContext: 'timesheet detail' },
  });
}

/** @registry A single timesheet resolved by its own id (WBS adjustment drill-down). */
export function useTimesheetById(timesheetId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.timesheets.byId(timesheetId ?? ''),
    queryFn: ({ signal }) =>
      timesheetApiClient.getTimesheet(
        { timesheetId: timesheetId! },
        toCancelToken(signal),
      ),
    enabled: !!timesheetId,
    staleTime: 0,
    meta: { errorContext: 'timesheet by id' },
  });
}
